/* eslint-disable import/no-duplicates */
"use client";
import React, { FC, useEffect, useState } from "react";

import { useTheme } from "next-themes";
import { LightAsync as SyntaxHighlighter } from "react-syntax-highlighter";
import css from "react-syntax-highlighter/dist/cjs/languages/hljs/css";
import html from "react-syntax-highlighter/dist/cjs/languages/hljs/htmlbars";
import js from "react-syntax-highlighter/dist/cjs/languages/hljs/javascript";
import jsx from "react-syntax-highlighter/dist/cjs/languages/hljs/javascript";
import a11yDark from "react-syntax-highlighter/dist/cjs/styles/hljs/a11y-dark";
import a11yLight from "react-syntax-highlighter/dist/cjs/styles/hljs/a11y-light";

import SkipCode from "./skip-code";

export interface CodeProps {
  translations: {
    codeBlock: string;
    codeBlockSkipped: string;
    skipCodeBlock: string;
  };
  /**
   * The code to be displayed.
   */
  value: {
    /**
     * The actual code content.
     */
    code: string;
    /**
     * The language of the code.
     */
    language: "jsx" | "javascript" | "html" | "css";
  };
}

const CustomPre = ({ children }: { children: React.ReactNode }) => {
  return (
    <pre className="rounded-lg border-2 border-gray-400 bg-gray-50 p-4 dark:bg-[rgb(43,_43,_43)]">
      {children}
    </pre>
  );
};
interface CustomCodeProps {
  children: React.ReactNode;
  translations: CodeProps["translations"];
}
const CustomCode = ({ children }: CustomCodeProps) => {
  return <code className="whitespace-pre-wrap">{children}</code>;
};

export const Code: FC<CodeProps> = ({
  translations,
  value: { code, language },
}) => {
  const { theme } = useTheme();
  const [preferredTheme, setPreferredTheme] = useState("dark");

  useEffect(() => {
    SyntaxHighlighter.registerLanguage("jsx", jsx);
    SyntaxHighlighter.registerLanguage("javascript", js);
    SyntaxHighlighter.registerLanguage("html", html);
    SyntaxHighlighter.registerLanguage("css", css);
  }, []);

  useEffect(() => {
    if (theme === "system") {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches)
        setPreferredTheme("dark");
      else if (window.matchMedia("(prefers-color-scheme: light)").matches)
        setPreferredTheme("light");
    } else if (theme) setPreferredTheme(theme);
  }, [theme]);

  if (!code) {
    return null;
  }

  return (
    <div className="relative max-w-prose sm:max-w-none">
      <SkipCode translations={translations}>
        <SyntaxHighlighter
          CodeTag={({ children }) => (
            <CustomCode translations={translations}>{children}</CustomCode>
          )}
          PreTag={({ children }) => <CustomPre>{children}</CustomPre>}
          language={language}
          lineProps={{
            style: { wordBreak: "break-all", whiteSpace: "pre-wrap" },
          }}
          style={preferredTheme === "light" ? a11yLight : a11yDark}
        >
          {code}
        </SyntaxHighlighter>
      </SkipCode>
    </div>
  );
};
export default Code;
